import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BACKEND, api, money } from "@/lib/api";
import { Spinner } from "@/components/RequireAuth";
import { Button } from "@/components/ui/button";
import { Printer, FileSpreadsheet, ArrowLeft } from "lucide-react";

const now = new Date();
const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
const MONTHS = Array.from({ length: 12 }, (_, i) => new Date(2000, i, 1).toLocaleString("en-US", { month: "long" }));

const LINES = [
  ["1", "Gross room rental revenue (rent + cleaning, after refunds)", "gross_rent"],
  ["2", "Less exempt revenue (nights after the first 30 of long stays)", "exempt_rent"],
  ["3", "Net taxable room revenue (line 1 - line 2)", "taxable_rent"],
  ["5", "Tax due (line 3 × line 4)", "tax_due"],
  ["6", "Tax you collected from guests through FairStay", "tax_collected"],
];

function ListingReturn({ r, i }) {
  return (
    <div className="break-inside-avoid rounded-2xl border p-5" data-testid={`filing-row-${i}`}>
      <div className="mb-3 flex flex-wrap justify-between gap-2">
        <div><p className="font-semibold">{r.listing}</p><p className="text-sm text-stone-600">{r.jurisdiction || "No area set"}</p></div>
        <div className="text-right text-sm"><p>Room tax permit: <b>{r.room_tax_permit || "missing"}</b></p><p>STR license: <b>{r.str_license || "missing"}</b></p></div>
      </div>
      <table className="w-full text-sm"><tbody>
        {LINES.slice(0, 3).map(([n, label, k]) => <tr key={k} className="border-t"><td className="w-10 py-2 text-stone-500">{n}</td><td>{label}</td><td className="text-right font-mono-num" data-testid={`filing-${k}-${i}`}>{money(r[k])}</td></tr>)}
        <tr className="border-t"><td className="py-2 text-stone-500">4</td><td>Tax rate</td><td className="text-right font-mono-num">{r.tax_rate}%</td></tr>
        {LINES.slice(3).map(([n, label, k]) => <tr key={k} className="border-t"><td className="py-2 text-stone-500">{n}</td><td>{label}</td><td className="text-right font-mono-num font-semibold" data-testid={`filing-${k}-${i}`}>{money(r[k])}</td></tr>)}
        <tr className="border-t"><td className="py-2 text-stone-500" /><td>Paid stays / room nights rented</td><td className="text-right font-mono-num">{r.stays} / {r.room_nights}</td></tr>
      </tbody></table>
    </div>
  );
}

// Printable monthly room tax summary laid out like the Clark County transient lodging tax return.
export default function TaxFiling() {
  const [year, setYear] = useState(prev.getFullYear());
  const [month, setMonth] = useState(prev.getMonth() + 1);
  const { data, isLoading, isError } = useQuery({ queryKey: ["tax-filing", year, month], queryFn: () => api.get(`/host/tax-filing?year=${year}&month=${month}`).then((r) => r.data) });
  const csv = `${BACKEND}/api/host/tax-filing?year=${year}&month=${month}&format=csv`;
  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8" data-testid="tax-filing-page">
      <Link to="/host" className="mb-4 inline-flex items-center gap-1 text-sm text-stone-600 hover:underline print:hidden" data-testid="tax-filing-back"><ArrowLeft className="h-4 w-4" />Back to Hosting</Link>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Room tax filing summary</h1>
          <p className="mt-1 text-stone-600" data-testid="tax-filing-period">{MONTHS[month - 1]} {year}{data?.owner && ` · ${data.owner}`}</p>
        </div>
        <div className="flex flex-wrap gap-2 print:hidden">
          <select className="rounded-xl border px-3 py-2 text-sm" value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="tax-filing-month">{MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select>
          <select className="rounded-xl border px-3 py-2 text-sm" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="tax-filing-year">{[0, 1, 2, 3].map((n) => now.getFullYear() - n).map((y) => <option key={y} value={y}>{y}</option>)}</select>
          <Button variant="outline" className="rounded-full" asChild data-testid="tax-filing-csv"><a href={csv}><FileSpreadsheet className="mr-1 h-4 w-4" />Spreadsheet</a></Button>
          <Button className="rounded-full" onClick={() => window.print()} data-testid="tax-filing-print"><Printer className="mr-1 h-4 w-4" />Print</Button>
        </div>
      </div>
      {isLoading && <Spinner />}
      {isError && <p className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-red-800" data-testid="tax-filing-error">We couldn't load this summary. Please try again.</p>}
      {data && (
        <div className="space-y-4">
          {data.rows.length ? data.rows.map((r, i) => <ListingReturn key={`${r.listing}-${i}`} r={r} i={i} />) : <p className="rounded-2xl border p-6 text-center text-stone-500" data-testid="tax-filing-empty">No listings yet.</p>}
          {data.rows.length > 1 && (
            <p className="rounded-2xl bg-stone-50 p-4 text-sm" data-testid="tax-filing-totals">All listings: taxable {money(data.totals.taxable_rent)} · tax due <b>{money(data.totals.tax_due)}</b> · collected {money(data.totals.tax_collected)}</p>
          )}
          <p className="text-xs text-stone-500">Stays count in the month they check in. File one return per permit with your city or county (usually monthly). The rate and exemptions come from FairStay's researched rules and are pending your tax advisor's sign-off, so check them against your return before filing.</p>
        </div>
      )}
    </div>
  );
}
